import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface ViewConnectionModalProps {
  open: boolean;
  onClose: () => void;
  adName: string;
  matchId?: string;
  clientSecret: string;
  onConnectionUpdated: () => void;
}

const ViewConnectionModal: React.FC<ViewConnectionModalProps> = ({
  open,
  onClose,
  adName,
  matchId,
  clientSecret,
  onConnectionUpdated
}) => {
  const [connectionType, setConnectionType] = useState<string>('vmix');
  const [vmixAddress, setVmixAddress] = useState('');
  const [vmixInputName, setVmixInputName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const isFormValid = connectionType && vmixAddress.trim() && vmixInputName.trim();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid) {
      setError('All fields are required');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const data = {
        connection_type: connectionType,
        vmix_address: vmixAddress.trim(),
        vmix_input_name: vmixInputName.trim()
      };

      // Build the URL
      const baseUrl = process.env.REACT_APP_SERVICE_URL || 'http://localhost:8000';
      const params = new URLSearchParams({
        client_secret: clientSecret
      });

      if (matchId) {
        params.append('match_id', matchId);
      }

      const url = `${baseUrl}/ads/${encodeURIComponent(adName)}/view_connection_data?${params.toString()}`;

      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update view connection data');
      }

      onConnectionUpdated();
      handleClose();
    } catch (error: any) {
      setError(error.message || 'Failed to update view connection data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setConnectionType('vmix');
      setVmixAddress('');
      setVmixInputName('');
      setError('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Configure View Connection
        <IconButton onClick={handleClose} disabled={isLoading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="connection-type-label">Connection Type</InputLabel>
              <Select
                labelId="connection-type-label"
                value={connectionType}
                onChange={(e) => setConnectionType(e.target.value)}
                label="Connection Type"
                disabled={isLoading}
              >
                <MenuItem value="vmix">vMix</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Vmix web site address"
              value={vmixAddress}
              onChange={(e) => setVmixAddress(e.target.value)}
              disabled={isLoading}
              placeholder="e.g., http://192.168.1.100:8088"
              sx={{ mb: 2 }}
              helperText="Enter the vMix web controller address"
            />

            <TextField
              fullWidth
              label="Vmix input name"
              value={vmixInputName}
              onChange={(e) => setVmixInputName(e.target.value)}
              disabled={isLoading}
              placeholder="e.g., Input1"
              helperText="Enter the vMix input name to control"
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading || !isFormValid}
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Saving...
            </>
          ) : (
            'Save Connection'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewConnectionModal;
